import { useEffect, useState } from 'react';
import { useRecoilState, useResetRecoilState } from 'recoil';
import type { AutoArea } from '~/lib/types';
import { damagePhotoUploadURLUniqueKeySelectorFamily } from '~/state';
import { useDeleteVSGAPhoto, useGetDamageBlob, usePostVSGAPhoto } from './api';

/**
 * `useDamageUpload` is used in combination with
 * components such as `CameraButton`to set the
 * base64 data and filename of an uploaded photo of damage.
 *
 * @param id unique name for file without file type
 * @returns
 */
export function useDamageUpload(id: string, area: AutoArea) {
  const { fetch: postPhoto, dataState: postDataStata } = usePostVSGAPhoto();
  const { fetch: deletePhoto, dataState: deleteDataState } =
    useDeleteVSGAPhoto();
  const { data, dataState: getDataState } = useGetDamageBlob(id);

  const [error, setError] = useState<string | undefined>(undefined);
  const [damageData, setDamageData] = useRecoilState(
    damagePhotoUploadURLUniqueKeySelectorFamily({ id })
  );

  const resetDamage = useResetRecoilState(
    damagePhotoUploadURLUniqueKeySelectorFamily({ id })
  );

  const handleImageBlob = async (file: File) => {
    if (error) {
      setError(undefined);
    }

    const formData = new FormData();

    formData.set('files', file);

    const postResponse = await postPhoto({
      params: {
        body: formData,
      },
    });

    const documentId = postResponse.documentId;
    const fileId = postResponse.fileInfos?.at(0)?.id;

    setDamageData((prev) => ({
      ...prev,
      filename: `${id}.jpeg`,
      id,
      area,
      documentId,
      fileId,
      imageURL: URL.createObjectURL(file),
    }));
  };

  function resetImageBlob() {
    if (damageData.documentId) {
      void deletePhoto({
        params: {
          vars: {
            documentId: damageData.documentId,
          },
        },
      }).then(() => {
        resetDamage();
      });
    }
  }

  useEffect(() => {
    if (data) {
      setDamageData((prev) => ({
        ...prev,
        imageURL: URL.createObjectURL(data),
      }));
    }
  }, [data, setDamageData]);

  const loading =
    postDataStata === 'loading' ||
    deleteDataState === 'loading' ||
    getDataState === 'loading';

  return {
    blobURL: damageData.imageURL,
    loading,
    error,
    handleImageBlob,
    resetImageBlob,
  };
}
